import React, { useState } from 'react';
import { Plus, PencilSimple, Power } from 'phosphor-react';
import { useApi, useFetch } from '_api';
import { useAlertActions } from '_actions';
import { formatVND } from '_helpers';
import { PageHeader, Bezel, Table, Button, IconButton, Modal, Input, Select, Tag, MotionReveal, StatCard } from '_ui';

// Manager catalog for session bundles ("Gói 10 buổi gội đầu"). Each type is tied to one service and
// grants N sessions for a fixed price. Editing a type never rewrites bundles already sold.

const perSession = (t) => (t.sessionCount > 0 ? Math.round(t.price / t.sessionCount) : 0);
const perSessionCommission = (t) => {
    const rate = t.service_ref ? t.service_ref.commissionRate : 0;
    return Math.round((perSession(t) * rate) / 100);
};

export function GoiDichVu() {
    const { data, loading, refetch } = useFetch('/api/package-types?all=true');
    const api = useApi();
    const alert = useAlertActions();
    const [editing, setEditing] = useState(null); // type object, {} for new, or null when closed
    const [showInactive, setShowInactive] = useState(false);

    const types = data || [];
    const active = types.filter(t => t.active);
    const rows = types.filter(t => showInactive || t.active);

    async function toggleActive(t) {
        const res = await api.put(`/api/package-types/${t._id}`, {
            name: t.name, serviceId: t.service_ref && t.service_ref._id, sessionCount: t.sessionCount, price: t.price, active: !t.active,
        });
        if (res.ok) { alert.success(t.active ? 'Đã ngừng gói' : 'Đã bật lại gói'); refetch(); }
        else alert.error('Không thể cập nhật', res.error);
    }

    const columns = [
        { key: 'name', header: 'Gói', render: t => <span className={t.active ? 'font-medium text-espresso' : 'text-espresso-soft/60 line-through'}>{t.name}</span> },
        { key: 'service', header: 'Dịch vụ', render: t => <span className="text-espresso-soft">{t.service_ref ? t.service_ref.name : '—'}</span> },
        { key: 'sessions', header: 'Số buổi', align: 'right', render: t => <span className="tnum">{t.sessionCount}</span> },
        { key: 'price', header: 'Giá gói', align: 'right', render: t => <span className="tnum">{formatVND(t.price)}</span> },
        { key: 'per', header: 'Mỗi buổi', align: 'right', render: t => <span className="tnum text-espresso-soft">{formatVND(perSession(t))}</span> },
        { key: 'com', header: 'HH/buổi', align: 'right', render: t => <span className="tnum text-sage-deep">{formatVND(perSessionCommission(t))}</span> },
        { key: 'status', header: 'Trạng thái', align: 'center', render: t => <Tag tone={t.active ? 'sage' : 'neutral'}>{t.active ? 'Đang bán' : 'Ngừng'}</Tag> },
        { key: 'actions', header: '', align: 'right', render: t => (
            <div className="flex justify-end gap-1">
                <IconButton icon={PencilSimple} label="Sửa" variant="ghost" onClick={() => setEditing(t)} />
                <IconButton icon={Power} label={t.active ? 'Ngừng' : 'Bật lại'} variant="ghost"
                    className={t.active ? 'hover:text-danger' : 'hover:text-sage-deep'} onClick={() => toggleActive(t)} />
            </div>
        ) },
    ];

    return (
        <div className="space-y-10">
            <PageHeader
                eyebrow="Quản lý"
                title="Gói dịch vụ"
                description="Gói nhiều buổi trả trước (VD: gói 10 buổi gội đầu). Sửa gói không ảnh hưởng gói đã bán."
                actions={
                    <div className="flex flex-wrap items-center gap-3">
                        <Button variant="ghost" size="sm" onClick={() => setShowInactive(v => !v)}>
                            {showInactive ? 'Ẩn gói đã ngừng' : 'Hiện gói đã ngừng'}
                        </Button>
                        <Button icon={Plus} onClick={() => setEditing({})}>Thêm gói</Button>
                    </div>
                }
            />

            <div className="grid gap-5 sm:grid-cols-2">
                <MotionReveal><StatCard label="Đang bán" value={active.length} /></MotionReveal>
                <MotionReveal delay={0.05}><StatCard label="Tổng gói" value={types.length} /></MotionReveal>
            </div>

            <Bezel className="overflow-hidden">
                <Table columns={columns} rows={rows} loading={loading} empty={showInactive ? 'Chưa có gói nào. Bấm “Thêm gói”.' : 'Không có gói đang bán.'} />
            </Bezel>

            {editing && <TypeModal type={editing} onClose={() => setEditing(null)} onSaved={() => { setEditing(null); refetch(); }} />}
        </div>
    );
}

function TypeModal({ type, onClose, onSaved }) {
    const isEdit = !!type._id;
    const { data: services } = useFetch('/api/services');
    const api = useApi();
    const alert = useAlertActions();
    const [form, setForm] = useState({
        name: type.name || '',
        serviceId: (type.service_ref && type.service_ref._id) || '',
        sessionCount: type.sessionCount ?? '',
        price: type.price ?? '',
    });
    const [errors, setErrors] = useState({});
    const [saving, setSaving] = useState(false);

    const svcOptions = (services || []).map(s => ({ value: s._id, label: `${s.name} · ${s.commissionRate}%` }));
    const svc = (services || []).find(s => String(s._id) === String(form.serviceId));
    const per = form.sessionCount > 0 && form.price !== '' ? Math.round(Number(form.price) / Number(form.sessionCount)) : 0;
    const perCom = svc ? Math.round((per * svc.commissionRate) / 100) : 0;

    function set(k, v) { setForm(f => ({ ...f, [k]: v })); }

    function validate() {
        const e = {};
        if (!form.name.trim()) e.name = 'Nhập tên gói';
        if (!form.serviceId) e.serviceId = 'Chọn dịch vụ';
        if (form.sessionCount === '' || Number(form.sessionCount) < 1) e.sessionCount = 'Ít nhất 1 buổi';
        if (form.price === '' || Number(form.price) < 0) e.price = 'Giá không hợp lệ';
        setErrors(e);
        return Object.keys(e).length === 0;
    }

    async function save() {
        if (!validate()) return;
        setSaving(true);
        const body = {
            name: form.name.trim(), serviceId: form.serviceId,
            sessionCount: Math.floor(Number(form.sessionCount)), price: Number(form.price),
        };
        const res = isEdit
            ? await api.put(`/api/package-types/${type._id}`, { ...body, active: type.active })
            : await api.post('/api/package-types', body);
        setSaving(false);
        if (res.ok) { alert.success(isEdit ? 'Đã lưu gói' : 'Đã thêm gói'); onSaved(); }
        else alert.error('Không thể lưu', res.error);
    }

    return (
        <Modal open onClose={onClose} eyebrow="Gói dịch vụ" title={isEdit ? 'Sửa gói' : 'Thêm gói'}
            footer={<><Button variant="ghost" onClick={onClose}>Huỷ</Button><Button onClick={save} loading={saving}>Lưu</Button></>}>
            <div className="space-y-5">
                <Input label="Tên gói" value={form.name} onChange={e => set('name', e.target.value)} error={errors.name} placeholder="VD: Gói 10 buổi gội đầu" />
                <Select label="Dịch vụ áp dụng" value={form.serviceId} onChange={v => set('serviceId', v)} options={svcOptions} error={errors.serviceId} placeholder="Chọn dịch vụ" />
                <div className="grid grid-cols-2 gap-4">
                    <Input label="Số buổi" type="number" min="1" value={form.sessionCount} onChange={e => set('sessionCount', e.target.value)} error={errors.sessionCount} placeholder="10" />
                    <Input label="Giá gói (VND)" type="number" min="0" value={form.price} onChange={e => set('price', e.target.value)} error={errors.price} placeholder="900000" />
                </div>
                {per > 0 && (
                    <div className="rounded-2xl bg-cream-200/50 px-4 py-3 text-sm ring-1 ring-espresso/10">
                        <div className="flex justify-between"><span className="text-espresso-soft">Giá mỗi buổi</span><span className="tnum text-espresso">{formatVND(per)}</span></div>
                        <div className="mt-1 flex justify-between"><span className="text-espresso-soft">Hoa hồng mỗi buổi</span><span className="tnum text-sage-deep">{formatVND(perCom)}</span></div>
                    </div>
                )}
            </div>
        </Modal>
    );
}
